<template>
    <div class="card bg-white p-3 rounded-lg shadow-sm">
        <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
            <div>
                <h3 class="font-bold text-base mb-1">Score Entry</h3>
                <p class="text-gray-500 text-sm">{{ subtitle }}</p>
            </div>
            <SelectButton v-if="availableModes.length > 1" v-model="mode" :options="availableModes" optionValue="value"
                dataKey="value" :allowEmpty="false" aria-labelledby="score-entry-mode" data-testid="score-entry-mode">
                <template #option="{ option }">
                    <span class="flex items-center gap-2"><i :class="option.icon" />{{ option.label }}</span>
                </template>
            </SelectButton>
        </div>

        <!-- Single subject: download a blank sheet, fill offline, upload back -->
        <div v-if="hasAnyAccess && mode === 'single'">
            <div class="flex items-start gap-2 text-xs text-gray-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3"
                data-testid="score-sheet-guidance">
                <i class="pi pi-info-circle text-amber-500 mt-0.5"></i>
                <span>
                    Always <strong>download a fresh sheet</strong> for the exact
                    {{ isCourseContext ? 'course' : labels.SUBJECT.toLowerCase() }} and calendar you are scoring,
                    <strong>edit only the score cells</strong>
                    (the rest of the sheet is locked — don't touch headers or the Student ID column),
                    and upload that same file back. Hand-made or reused sheets will be rejected.
                </span>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
                <div class="flex flex-col gap-1.5">
                    <label class="font-bold text-sm">{{ labels.CLASS }}</label>
                    <ClassroomSectionSelect v-model="classroomId" className="w-full" :showClassroomsOnly="true" />
                </div>
                <div class="flex flex-col gap-1.5">
                    <label class="font-bold text-sm" v-if="!isCourseContext">{{ labels.SUBJECT }}</label>
                    <label class="font-bold text-sm" v-else>Course</label>

                    <SubjectSelect v-if="!isCourseContext" v-model="subjectId" className="w-full" :disabled="!classroomId" />
                    <Select v-else-if="!isAdmin" v-model="subjectId" :options="myCourseOptions" optionLabel="label" optionValue="value"
                        placeholder="Select Course" class="w-full" filter showClear data-testid="select-my-course">
                        <template #option="{ option }">
                            <div class="flex flex-col py-0.5">
                                <span class="text-sm font-medium">{{ option.code }} — {{ option.title }}</span>
                                <span class="text-xs text-gray-400">Unit: {{ option.unit }} · {{ formatCourseInstance({ course_code: option.code, course_instance_id: option.instance }) || '—' }}</span>
                            </div>
                        </template>
                    </Select>
                    <CourseSelect v-else v-model="subjectId" className="w-full" />
                </div>
                <div class="flex flex-col gap-1.5">
                    <label class="font-bold text-sm">Academic Calendar</label>
                    <AcademicCalendarSelect v-model="academicCalendarId" :placeholder="`Select ${labels.TERM}`" className="w-full" />
                    <small v-if="calendarMismatch" class="text-amber-600 flex items-start gap-1" data-testid="calendar-mismatch-note">
                        <i class="pi pi-exclamation-triangle mt-0.5"></i>
                        <span>This is not the school's <strong>active</strong> calendar. Scores will save to the selected calendar
                            and only show on score views when that calendar is being viewed.</span>
                    </small>
                </div>
            </div>
            <div v-if="isCourseContext" class="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
                <div class="flex flex-col gap-1.5">
                    <label class="font-bold text-sm">Programme <span class="text-gray-400 font-normal text-xs">(optional)</span></label>
                    <ProgrammeSelect v-model="programmeId" size="small" placeholder="All programmes" className="w-full" showClear />
                </div>
            </div>

            <div class="flex flex-col md:flex-row gap-3">
                <!-- Download Section -->
                <div class="flex-1 border p-3 rounded-lg bg-gray-50">
                    <h4 class="font-bold mb-1.5 text-sm">1. Download Score Sheet</h4>
                    <p class="text-xs text-gray-500 mb-2">
                        Get the Excel template for {{ selectedSubjectName || 'selected subject' }}.
                    </p>
                    <Button label="Download Template" icon="pi pi-download" size="small" @click="downloadSheet" :loading="downloading"
                        :disabled="!canDownload" data-testid="btn-download-score-template" />
                </div>

                <!-- Upload Section -->
                <div class="flex-1 border p-3 rounded-lg bg-gray-50 flex flex-col"
                    :class="{ 'opacity-50 pointer-events-none': !canDownload }">
                    <h4 class="font-bold mb-1.5 text-sm">2. Upload Scores</h4>
                    <p class="text-xs text-gray-500 mb-2">
                        Upload the filled Excel file properly.
                    </p>

                    <div class="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center transition-colors cursor-pointer hover:bg-gray-100 flex-1 flex flex-col justify-center items-center gap-2 relative"
                        :class="{ 'border-primary bg-primary/10': isDragging }" @dragover.prevent="isDragging = true"
                        @dragleave.prevent="isDragging = false" @drop.prevent="onDrop" @click="triggerFileSelect">
                        <input type="file" ref="fileInput" class="hidden" accept=".xlsx, .xls" @change="onFileChange" />

                        <div v-if="!selectedFile">
                            <i class="pi pi-cloud-upload text-2xl text-gray-400 mb-1.5"></i>
                            <p class="text-gray-600 font-medium text-sm">Drag & Drop Excel File Here</p>
                            <p class="text-xs text-gray-400 mt-0.5">or click to browse</p>
                        </div>

                        <div v-else class="flex flex-col items-center">
                            <i class="pi pi-file-excel text-2xl text-primary mb-1.5"></i>
                            <p class="font-medium text-gray-800 break-all text-sm">{{ selectedFile.name }}</p>
                            <p class="text-xs text-gray-500">{{ formatFileSize(selectedFile.size) }}</p>
                            <Button icon="pi pi-times"
                                class="p-button-rounded p-button-text p-button-danger p-button-sm absolute top-2 right-2"
                                data-testid="btn-remove-selected-file"
                                @click.stop="selectedFile = null" />
                        </div>
                    </div>

                    <ScoreUploadPreview :previewing="previewing" :preview-error="previewError" :summary="previewSummary"
                        :is-course-context="isCourseContext" />

                    <Button :label="uploadButtonLabel(previewSummary)"
                        icon="pi pi-upload" class="w-full mt-3"
                        size="small" @click="uploadSheet" :loading="uploading"
                        :disabled="isUploadDisabled(!!selectedFile, previewing, previewSummary)"
                        data-testid="btn-upload-scores" />
                </div>
            </div>

            <!-- CA Approval Section (tertiary course context only). Lives inside the
                 single-subject panel so it does not sit between the mode v-if and its
                 v-else-if — Vue chains v-else-if to the immediately preceding element. -->
            <div v-if="isCourseContext && subjectId && academicCalendarId"
              class="mt-4 border rounded-lg p-4 bg-amber-50/50 border-amber-200">
              <div class="flex items-center justify-between">
                <div>
                  <h4 class="font-bold text-sm text-amber-900">CA Score Release</h4>
                  <p class="text-xs text-gray-600 mt-1">
                    Approve CA scores so students can view their provisional CA results before senate approval.
                  </p>
                </div>
                <div class="flex items-center gap-2">
                  <Button
                    v-if="!caApproved"
                    label="Approve CA for Release"
                    icon="pi pi-check-circle"
                    severity="success"
                    size="small"
                    :loading="approvingCa"
                    data-testid="btn-approve-ca"
                    @click="handleApproveCa"
                  />
                  <template v-else>
                    <Tag value="CA Released" severity="success" />
                    <Button
                      label="Revoke"
                      icon="pi pi-times"
                      severity="warn"
                      size="small"
                      text
                      :loading="approvingCa"
                      data-testid="btn-revoke-ca"
                      @click="handleRevokeCa"
                    />
                  </template>
                </div>
              </div>
            </div>
        </div>

        <!-- Bulk export: download many sheets at once (export-only — no bulk upload) -->
        <div v-else-if="hasAnyAccess && mode === 'bulk'">
            <div class="flex items-start gap-2 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 mb-4"
                data-testid="bulk-no-upload-note">
                <i class="pi pi-info-circle text-amber-500 mt-0.5"></i>
                <span>Bulk export is <strong>download-only</strong> — we don't support bulk upload. Fill each sheet, then switch
                    to <strong>Single subject</strong> to upload them one at a time.</span>
            </div>
            <BulkExportPanel :modes="bulkModes" />
        </div>

        <!-- No access at all -->
        <div v-else class="flex flex-col items-center justify-center p-8 text-center text-gray-500">
            <i class="pi pi-lock text-2xl mb-3"></i>
            <p class="text-base font-medium">Access Denied</p>
            <p class="text-sm">You do not have permission to enter scores.</p>
        </div>

        <ScoreUploadResultDialog v-model:visible="showResultDialog" :result="importResult"
            :view-scores-route="lecturerCourseRoute" @download-issues="downloadIssuesCsv" />
    </div>
</template>

<script setup lang="ts">
import { gql } from '@apollo/client/core';
import { useApolloClient } from '@vue/apollo-composable';
import { useToast } from 'primevue/usetoast';
import ClassroomSectionSelect from '@/components/common/ClassroomSectionSelect.vue';
import AcademicCalendarSelect from '@/components/common/AcademicCalendarSelect.vue';
import SubjectSelect from '@/components/common/SubjectSelect.vue';
import CourseSelect from '@/components/common/CourseSelect.vue';
import ProgrammeSelect from '@/components/common/ProgrammeSelect.vue';
import BulkExportPanel from '@/components/common/BulkExport/BulkExportPanel.vue';
import type { BulkExportMode } from '@/composables/useBulkExport';
import { useScoreEntry } from '@/composables/useScoreEntry';
import ScoreUploadPreview from './ScoreUploadPreview.vue';
import ScoreUploadResultDialog from './ScoreUploadResultDialog.vue';
import { isUploadDisabled, uploadButtonLabel } from './scoreUploadSummary';
import { can, AppPermissions } from '@/utils/permissions';
import { formatCourseInstance } from '@/utils/courseInstance';

const {
  currentUser,
  classroomId,
  subjectId,
  academicCalendarId,
  programmeId,
  downloading,
  uploading,
  selectedFile,
  isDragging,
  fileInput,
  labels,
  isCourseContext,
  isAdmin,
  myCourseOptions,
  selectedSubjectName,
  canDownload,
  hasScorePermission,
  calendarMismatch,
  lecturerCourseRoute,
  previewing,
  previewSummary,
  previewError,
  importResult,
  showResultDialog,
  downloadSheet,
  triggerFileSelect,
  onFileChange,
  onDrop,
  formatFileSize,
  uploadSheet,
  downloadIssuesCsv,
} = useScoreEntry();

const bulkModes: BulkExportMode[] = ['score-sheets', 'cbt-scores', 'merged'];

// Bulk export carries its own permissions (independent of score entry), so a
// user may have one, the other, or both. Only offer the toggle options they
// can actually use, and never trap a bulk-only user behind "Access Denied".
const canBulkExport = computed(() =>
  can(currentUser.value, AppPermissions.Results.Actions.BulkDownloadScores, AppPermissions.Results.Resource) ||
  can(currentUser.value, AppPermissions.Results.Actions.BulkDownloadCbt, AppPermissions.Results.Resource),
);

const availableModes = computed(() => {
  const opts: Array<{ label: string; value: 'single' | 'bulk'; icon: string }> = [];
  if (hasScorePermission.value) opts.push({ label: 'Single subject', value: 'single', icon: 'pi pi-file-edit' });
  if (canBulkExport.value) opts.push({ label: 'Bulk export', value: 'bulk', icon: 'pi pi-download' });
  return opts;
});

const hasAnyAccess = computed(() => hasScorePermission.value || canBulkExport.value);

const mode = ref<'single' | 'bulk'>(hasScorePermission.value ? 'single' : 'bulk');

const subtitle = computed(() =>
  mode.value === 'bulk'
    ? 'Export score sheets for many courses at once. Bulk is download-only — upload filled sheets one subject at a time from Single subject.'
    : 'Download a score sheet for a specific subject, fill it offline, and upload it back to record scores.',
);

// --- CA Score Approval ---
const toast = useToast();
const { resolveClient } = useApolloClient();
const approvingCa = ref(false);
const caApproved = ref(false);

const APPROVE_CA = gql`
  mutation ApproveCaScoresForCourse($course_id: ID!, $academic_calendar_id: ID!) {
    approveCaScoresForCourse(course_id: $course_id, academic_calendar_id: $academic_calendar_id) {
      success
      message
    }
  }
`;

const REVOKE_CA = gql`
  mutation RevokeCaScoresForCourse($course_id: ID!, $academic_calendar_id: ID!) {
    revokeCaScoresForCourse(course_id: $course_id, academic_calendar_id: $academic_calendar_id) {
      success
      message
    }
  }
`;

// Check CA approval status when course/calendar changes
watch([() => subjectId.value, () => academicCalendarId.value], async([courseId, calId]) => {
  if (!courseId || !calId || !isCourseContext.value) {
    caApproved.value = false;
    return;
  }
  try {
    const client = resolveClient();
    const { data } = await client.query({
      query: gql`
        query CheckCaStatus($course_id: ID!, $academic_calendar_id: ID!) {
          checkCaApprovalStatus(course_id: $course_id, academic_calendar_id: $academic_calendar_id)
        }
      `,
      variables: { course_id: courseId, academic_calendar_id: calId },
      fetchPolicy: 'network-only',
    });
    caApproved.value = data?.checkCaApprovalStatus === true;
  } catch {
    caApproved.value = false;
  }
}, { immediate: true });

async function handleApproveCa() {
  if (!subjectId.value || !academicCalendarId.value) return;
  approvingCa.value = true;
  try {
    const client = resolveClient();
    const { data } = await client.mutate({
      mutation: APPROVE_CA,
      variables: { course_id: subjectId.value, academic_calendar_id: academicCalendarId.value },
    });
    if (data?.approveCaScoresForCourse?.success) {
      caApproved.value = true;
      toast.add({ severity: 'success', summary: 'CA Released', detail: data.approveCaScoresForCourse.message, life: 4000 });
    }
  } catch (e: unknown) {
    toast.add({ severity: 'error', summary: 'Error', detail: e instanceof Error ? e.message : 'Failed to approve CA scores.', life: 5000 });
  } finally {
    approvingCa.value = false;
  }
}

async function handleRevokeCa() {
  if (!subjectId.value || !academicCalendarId.value) return;
  approvingCa.value = true;
  try {
    const client = resolveClient();
    const { data } = await client.mutate({
      mutation: REVOKE_CA,
      variables: { course_id: subjectId.value, academic_calendar_id: academicCalendarId.value },
    });
    if (data?.revokeCaScoresForCourse?.success) {
      caApproved.value = false;
      toast.add({ severity: 'success', summary: 'CA Revoked', detail: data.revokeCaScoresForCourse.message, life: 4000 });
    }
  } catch (e: unknown) {
    toast.add({ severity: 'error', summary: 'Error', detail: e instanceof Error ? e.message : 'Failed to revoke CA approval.', life: 5000 });
  } finally {
    approvingCa.value = false;
  }
}
</script>
